import { useState } from 'react';
import { motion } from 'framer-motion';

export type AvatarGender = 'male' | 'female';

interface AvatarProps {
  /** Display name — used as the seed so a person's avatar is stable across visits. */
  name?: string;
  /**
   * Drives the DiceBear style: a gendered illustration for male/female, and a
   * friendly neutral style when gender is unknown or not disclosed. Both are
   * deterministic per seed, so the same person always gets the same face.
   */
  gender?: AvatarGender | null;
  /** Diameter in pixels. */
  size?: number;
  /** Makes the avatar interactive; rendered as a button so keyboard users can reach it. */
  onClick?: () => void;
  className?: string;
}

function seedOf(name: string | undefined): string {
  return name?.trim() || 'CampusOne';
}

export function Avatar({ name, gender, size = 40, onClick, className = '' }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const seed = encodeURIComponent(seedOf(name));

  const gendered = gender === 'male' || gender === 'female';
  const style = gendered ? 'avataaars' : 'adventurer-neutral';
  const query = gendered
    ? `seed=${seed}&gender=${gender}`
    : `seed=${seed}`;
  const src = `https://api.dicebear.com/9.x/${style}/svg?${query}`;

  const initials = seedOf(name).charAt(0).toUpperCase() || '?';

  const classes = `inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500 ring-1 ring-slate-900/10 ${className}`;
  const styleBox = { width: size, height: size };

  if (onClick) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        className={`${classes} cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sapBrandColor)]`}
        style={styleBox}
        aria-label={seedOf(name)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.93 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      >
        {failed ? (
          <span className="text-base font-semibold">{initials}</span>
        ) : (
          <img
            src={src}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
            onError={() => setFailed(true)}
          />
        )}
      </motion.button>
    );
  }

  return (
    <div className={classes} style={styleBox} aria-hidden="true">
      {failed ? (
        <span className="text-base font-semibold">{initials}</span>
      ) : (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}